import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ShoppingCart, Plus, Minus, Trash2, ArrowRight, ShoppingBag, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

export const CartDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, cartTotal } = useCart();
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && cart.items && cart.items.length > 0) {
      fetchProducts();
    }
  }, [isOpen, cart.items]);

  const fetchProducts = async () => {
    if (!cart.items || cart.items.length === 0) return;
    setLoading(true);
    try {
      const productIds = cart.items.map(item => item.product_id);
      const responses = await Promise.all(
        productIds.map(id => axios.get(`${API}/products/${id}`))
      );
      const productsMap = {};
      responses.forEach(res => {
        productsMap[res.data.id] = res.data;
      });
      setProducts(productsMap);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      await updateQuantity(productId, newQuantity);
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  const handleGoToCart = () => {
    onClose();
    navigate('/cart');
  };

  const handleGoToCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    onClose();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const itemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div 
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="bg-[#0f172a] p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                  <ShoppingCart className="h-5 w-5 text-cyan-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Meu Carrinho</h2>
                  <p className="text-slate-400 text-sm">{itemCount} {itemCount === 1 ? 'item' : 'itens'}</p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-slate-700 rounded-xl transition-colors"
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : !cart.items || cart.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <div className="w-24 h-24 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-6">
                  <ShoppingBag className="h-12 w-12 text-slate-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  Carrinho vazio
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-6">
                  Adicione produtos para começar suas compras
                </p>
                <Button 
                  onClick={handleContinueShopping}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full px-6"
                >
                  Explorar Loja
                </Button>
              </div>
            ) : (
              <div className="p-4 space-y-4">
                {cart.items.map(item => {
                  const product = products[item.product_id];
                  if (!product) return null;
                  return (
                    <div 
                      key={item.product_id}
                      className="bg-slate-50 dark:bg-slate-800 rounded-2xl p-4 flex gap-4"
                    >
                      {/* Image */}
                      <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-white dark:bg-slate-700">
                        <img 
                          src={product.images[0] || 'https://via.placeholder.com/80'}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-slate-900 dark:text-white text-sm line-clamp-2 pr-2">
                            {product.name}
                          </h4>
                          <button
                            onClick={() => handleRemove(item.product_id)}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        
                        <p className="text-cyan-600 dark:text-cyan-400 font-bold mb-3">
                          {formatCurrency(item.price * item.quantity)}
                        </p>
                        
                        {/* Quantity Controls */}
                        <div className="flex items-center gap-1 bg-white dark:bg-slate-900 rounded-lg p-1 w-fit">
                          <button
                            onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-8 text-center font-semibold text-sm">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {cart.items && cart.items.length > 0 && (
            <div className="border-t border-slate-200 dark:border-slate-700 p-6 bg-white dark:bg-slate-900">
              {/* Subtotal */}
              <div className="flex justify-between items-center mb-4">
                <span className="text-slate-600 dark:text-slate-400">Subtotal</span>
                <span className="text-2xl font-bold text-slate-900 dark:text-white">
                  {formatCurrency(cartTotal)}
                </span>
              </div>
              
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                Frete calculado no checkout
              </p>
              
              {/* Buttons */}
              <div className="space-y-3">
                <Button 
                  onClick={handleGoToCart}
                  className="w-full h-12 bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-xl shadow-lg"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Finalizar Compra
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                
                <Button 
                  onClick={handleGoToCheckout}
                  variant="outline"
                  className="w-full h-12 rounded-xl"
                >
                  Ir para Checkout
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
